import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable, forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { mergeMap } from 'rxjs/operators';
import {
  ISubProgramSecurityBondsApiResponse,
  ICanAddBonds,
  IBusSecReqApiResponse,
  ISecurityTotalReqApiResponse,
  ISelectedBondResponse,
  IBondApiRequest,
  IBondSubmitResponse,
  IBondListResponse,
} from '../models/financial-security-api.model';
import {
  IBondDetails,
  ISelectedBondDetail,
  ISecurityRequirementUpdateRequest,
  BondTypes,
} from '../models/financial-security.model';
import { HttpParams } from '@angular/common/http';
import { IBaseResponse } from '@carm/_utils/models';
import { DatePipe } from '@angular/common';
import { LoaderSpinnerTypes } from '@carm/_core';

@Injectable()
export class FinancialSecurityService extends BaseApiService {
  constructor(protected injector: Injector, private datePipe: DatePipe) {
    super(injector);
  }

  get serviceBaseUrlBP(): string {
    return `${this.endPointBP}/financial/security`;
  }

  get serviceBaseUrlB(): string {
    return `${this.endPointB}/financial/security`;
  }

  get serviceBaseUrlNB(): string {
    return `${this.getBaseUrl}/financial/security`;
  }

  private get getBaseUrl(): string {
    return environment.baseApiUrls;
  }

  public submitUpdateRPPRequirement(
    request: ISecurityRequirementUpdateRequest
  ): Observable<IBaseResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/rpp-requirement`;
    return this.httpAuthPut<IBaseResponse>(endPoint, request);
  }

  public getBondsInformation(): Observable<ISubProgramSecurityBondsApiResponse> {
    const endPoint = `${this.serviceBaseUrlB}/bonds`;
    return this.httpAuthGet<ISubProgramSecurityBondsApiResponse>(endPoint);
  }

  public canAddBonds(): Observable<ICanAddBonds> {
    const endPoint = `${this.serviceBaseUrlB}/can-add-bonds`;
    return this.httpAuthGet<ICanAddBonds>(endPoint);
  }

  public submitBondForProgram(
    bond: IBondDetails
  ): Observable<any> {
    const endPoint = `${this.serviceBaseUrlBP}/bond/submit`;
    return this.httpAuthPost<any>(endPoint, bond);
  }

  public getSubmittedBondForProgram(
    bn15: string,
    bondtype: string,
    number: string
  ): Observable<ISelectedBondResponse> {
    bondtype = bondtype === 'Cash' ? 'cash-bond' : 'e-bond';
    const endPoint = `${this.serviceBaseUrlBP}/${bondtype}/bond-id/${number}?bondProgramId=${bn15}`;
    return this.httpAuthGet<ISelectedBondResponse>(endPoint);
  }

  public updateBondForProgram(change: IBondApiRequest): Observable<IBaseResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/bond/update`;
    return this.httpAuthPut<IBaseResponse>(endPoint, change);
  }

  public submitActionBond(
    action: string,
    bond: ISelectedBondDetail
  ): Observable<IBondSubmitResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/bond/` + action;
    const req = {
      bn15: bond.bn15,
      id: bond.id,
      type: bond.type,
      suretyProvider: bond.suretyProvider,
    };
    if (bond.type !== 'Cash') {
      req.suretyProvider = bond.suretyProvider;
    }
    return this.httpAuthPut<any>(endPoint, req);
  }

  public getBusinessSecurityRequirements(): Observable<any[]> {
    const params = new HttpParams()
      .set('currentPage', '1')
      .set('pageSize', '2000')
      .set('sort', 'creationtime:desc');
    return this.userService.getUserBusinessId().pipe(
      mergeMap((businessUid: string) => {
        params.set('businessId', businessUid);
        return forkJoin<
          IBusSecReqApiResponse,
          IBusSecReqApiResponse,
          ISecurityTotalReqApiResponse
        >([this.getrpp(params), this.getNonrpp(params), this.getTotal(params)]);
      })
    );
  }

  public getrpp(params: HttpParams): Observable<IBusSecReqApiResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/business-security-requirements/rpp`;
    return this.httpAuthGet<IBusSecReqApiResponse>(endPoint, params);
  }

  public getNonrpp(params: HttpParams): Observable<IBusSecReqApiResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/business-security-requirements/non-rpp`;
    return this.httpAuthGet<IBusSecReqApiResponse>(endPoint, params);
  }

  public getTotal(
    params: HttpParams
  ): Observable<ISecurityTotalReqApiResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/business-security-requirements/totals`;
    return this.httpAuthGet<ISecurityTotalReqApiResponse>(endPoint, params);
  }

  public getBonds(
    minDate: Date,
    maxDate: Date,
    pageIndex: number,
    pageSize: number,
    sortField: string,
    bondType: BondTypes
  ): Observable<IBondListResponse> {
    const params = new HttpParams()
      .set('currentPage', pageIndex)
      .set('pageSize', pageSize)
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'))
      .set('bondsSort', sortField);
    const bondsType = bondType === BondTypes.Cash ? 'cash-bond' : 'e-bond';
    const endPoint = `${this.serviceBaseUrlB}/${bondsType}/list?fields=DEFAULT`;
    return this.httpAuthGet<IBondListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }
}
